/**
 * ノンパラメトリック検定（Mann–Whitney / Wilcoxon / Kruskal–Wallis）
 * 式の参照: docs/systems/keisuke/REQUIREMENTS.md §5.4
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';
import { normalCDF } from './probit';

export type NonparametricTestMethod = 'mann-whitney' | 'wilcoxon' | 'kruskal-wallis';

function median(values: number[]): number {
  if (values.length === 0) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]!
    : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** 全値に平均順位（タイは中間順位）を付与 */
function assignAverageRanks(
  entries: Array<{ value: number; groupIndex: number }>
): Array<{ value: number; groupIndex: number; rank: number }> {
  const sorted = [...entries].sort((a, b) => a.value - b.value);
  const withRank: Array<{ value: number; groupIndex: number; rank: number }> = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1]!.value === sorted[i]!.value) {
      j += 1;
    }
    const avgRank = (i + 1 + j + 1) / 2;
    for (let k = i; k <= j; k += 1) {
      withRank.push({ ...sorted[k]!, rank: avgRank });
    }
    i = j + 1;
  }
  return withRank;
}

/**
 * タイ補正項 Σ(tⱼ³ − tⱼ) を計算する。tⱼ は同順位グループ j に属する値の個数。
 * 同順位が無ければ 0 を返す（補正なし）。R の wilcox.test / kruskal.test と同じ定義。
 */
function tieCorrectionSum(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  let sum = 0;
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1]! === sorted[i]!) {
      j += 1;
    }
    const t = j - i + 1;
    if (t > 1) {
      sum += t * t * t - t;
    }
    i = j + 1;
  }
  return sum;
}

function chiSquareUpperTailP(chiSq: number, df: number): number {
  if (df <= 0 || chiSq < 0) return 1;
  if (chiSq === 0) return 1;
  if (df === 1) {
    const z = Math.sqrt(chiSq);
    return 2 * (1 - normalCDF(z));
  }
  if (df >= 30) {
    const mean = df;
    const stdDev = Math.sqrt(2 * df);
    const z = (chiSq - mean) / stdDev;
    return 1 - normalCDF(z);
  }
  const h = Math.pow(chiSq / df, 1 / 3);
  const mu = 1 - 2 / (9 * df);
  const sigma = Math.sqrt(2 / (9 * df));
  const z = (h - mu) / sigma;
  return 1 - normalCDF(z);
}

export interface MannWhitneyResult {
  U: number;
  z: number;
  pValue: number;
  n1: number;
  n2: number;
  median1: number;
  median2: number;
}

/**
 * Mann–Whitney U 検定（対応なし2群、両側）
 */
export function mannWhitneyUTest(group1: number[], group2: number[]): MannWhitneyResult {
  const g1 = group1.filter((v) => Number.isFinite(v));
  const g2 = group2.filter((v) => Number.isFinite(v));
  if (g1.length < 2 || g2.length < 2) {
    throw new KeisukeAdapterError('mannWhitneyMinTwoPerGroup');
  }
  const entries = [
    ...g1.map((value) => ({ value, groupIndex: 0 })),
    ...g2.map((value) => ({ value, groupIndex: 1 })),
  ];
  const ranked = assignAverageRanks(entries);
  let R1 = 0;
  for (const r of ranked) {
    if (r.groupIndex === 0) R1 += r.rank;
  }
  const n1 = g1.length;
  const n2 = g2.length;
  const U1 = R1 - (n1 * (n1 + 1)) / 2;
  const U2 = n1 * n2 - U1;
  const U = Math.min(U1, U2);
  const mu = (n1 * n2) / 2;
  // 同順位（タイ）補正を含む分散（R wilcox.test の正規近似と同じ）:
  // σ² = (n1·n2/12)·[(N+1) − Σ(tⱼ³−tⱼ)/(N(N−1))]、N = n1+n2
  const N = n1 + n2;
  const tieSum = tieCorrectionSum(entries.map((e) => e.value));
  const variance =
    N > 1 ? ((n1 * n2) / 12) * (N + 1 - tieSum / (N * (N - 1))) : 0;
  const sigma = variance > 0 ? Math.sqrt(variance) : 0;
  const z = sigma > 0 ? (U - mu) / sigma : 0;
  const pValue = Math.min(1, Math.max(0, 2 * (1 - normalCDF(Math.abs(z)))));
  return {
    U,
    z,
    pValue,
    n1,
    n2,
    median1: median(g1),
    median2: median(g2),
  };
}

export interface WilcoxonSignedRankResult {
  W: number;
  z: number;
  pValue: number;
  n: number;
  medianDiff: number;
}

/**
 * Wilcoxon の符号付順位検定（対応あり2群＝差分、両側）
 */
export function wilcoxonSignedRankTest(before: number[], after: number[]): WilcoxonSignedRankResult {
  if (before.length !== after.length || before.length < 2) {
    throw new KeisukeAdapterError('wilcoxonPairedMinTwo');
  }
  const diffs: number[] = [];
  for (let i = 0; i < before.length; i += 1) {
    const d = after[i]! - before[i]!;
    if (Number.isFinite(d) && Math.abs(d) > 1e-15) diffs.push(d);
  }
  if (diffs.length < 2) {
    throw new KeisukeAdapterError('wilcoxonAllZeroDiff');
  }
  const entries = diffs.map((value) => ({
    value: Math.abs(value),
    sign: value > 0 ? 1 : -1,
  }));
  const sorted = [...entries].sort((a, b) => a.value - b.value);
  let i = 0;
  let Wpos = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1]!.value === sorted[i]!.value) {
      j += 1;
    }
    const avgRank = (i + 1 + j + 1) / 2;
    for (let k = i; k <= j; k += 1) {
      if (sorted[k]!.sign > 0) Wpos += avgRank;
    }
    i = j + 1;
  }
  const n = diffs.length;
  const mu = (n * (n + 1)) / 4;
  // 同順位（タイ）補正を含む分散（R wilcox.test の正規近似と同じ）:
  // σ² = n(n+1)(2n+1)/24 − Σ(tⱼ³−tⱼ)/48。タイは絶対差の順位で評価する。
  const tieSum = tieCorrectionSum(diffs.map((d) => Math.abs(d)));
  const variance = (n * (n + 1) * (2 * n + 1)) / 24 - tieSum / 48;
  const sigma = variance > 0 ? Math.sqrt(variance) : 0;
  const z = sigma > 0 ? (Wpos - mu) / sigma : 0;
  const pValue = Math.min(1, Math.max(0, 2 * (1 - normalCDF(Math.abs(z)))));
  const pairedDiffs = before.map((b, idx) => after[idx]! - b);
  return { W: Wpos, z, pValue, n, medianDiff: median(pairedDiffs) };
}

export interface KruskalWallisResult {
  H: number;
  pValue: number;
  df: number;
  groups: Array<{ name: string; n: number; median: number; meanRank: number }>;
}

/**
 * Kruskal–Wallis 検定（3群以上）
 */
export function kruskalWallisTest(
  groups: Array<{ name: string; values: number[] }>
): KruskalWallisResult {
  const valid = groups
    .map((g) => ({
      name: g.name,
      values: g.values.filter((v) => Number.isFinite(v)),
    }))
    .filter((g) => g.values.length > 0);
  if (valid.length < 3) {
    throw new KeisukeAdapterError('kruskalMinThreeGroups');
  }
  const entries = valid.flatMap((g, groupIndex) =>
    g.values.map((value) => ({ value, groupIndex }))
  );
  const ranked = assignAverageRanks(entries);
  const k = valid.length;
  const N = ranked.length;
  const sumRanks = new Array(k).fill(0);
  const counts = new Array(k).fill(0);
  for (const r of ranked) {
    sumRanks[r.groupIndex] += r.rank;
    counts[r.groupIndex] += 1;
  }
  let H = 0;
  for (let i = 0; i < k; i += 1) {
    const ni = counts[i]!;
    H += (sumRanks[i]! * sumRanks[i]!) / ni;
  }
  H = (12 / (N * (N + 1))) * H - 3 * (N + 1);
  // 同順位（タイ）補正（R kruskal.test と同じ）: H' = H / (1 − Σ(tⱼ³−tⱼ)/(N³−N))
  const tieSum = tieCorrectionSum(ranked.map((r) => r.value));
  const tieFactor = N > 1 ? 1 - tieSum / (N * N * N - N) : 1;
  if (tieFactor > 0) {
    H /= tieFactor;
  }
  const df = k - 1;
  const pValue = chiSquareUpperTailP(H, df);
  const groupStats = valid.map((g, i) => ({
    name: g.name,
    n: counts[i]!,
    median: median(g.values),
    meanRank: sumRanks[i]! / counts[i]!,
  }));
  return { H, pValue, df, groups: groupStats };
}

/** Mann–Whitney 結果を共通表示形へ（偽 df／SD／NaN CI を作らない） */
export function mannWhitneyToTTestShape(mw: MannWhitneyResult): {
  t: number;
  pValue: number;
  pValueOneSided: number;
  mean1: number;
  mean2: number;
  n1: number;
  n2: number;
  method: 'mann-whitney';
} {
  return {
    t: mw.z,
    pValue: mw.pValue,
    pValueOneSided: mw.pValue / 2,
    mean1: mw.median1,
    mean2: mw.median2,
    n1: mw.n1,
    n2: mw.n2,
    method: 'mann-whitney',
  };
}

export function wilcoxonToTTestShape(
  w: WilcoxonSignedRankResult,
  before: number[],
  after: number[]
): {
  t: number;
  pValue: number;
  pValueOneSided: number;
  mean1: number;
  mean2: number;
  n1: number;
  n2: number;
  method: 'wilcoxon';
} {
  return {
    t: w.z,
    pValue: w.pValue,
    pValueOneSided: w.pValue / 2,
    mean1: median(before),
    mean2: median(after),
    n1: w.n,
    n2: w.n,
    method: 'wilcoxon',
  };
}
